/**
 * "Compare with" on a phone (docs/mobile-editor-layout-spec.md 14.4, which
 * supersedes 11.8's card): an iOS sheet sized to its rows, full width in
 * portrait and 360 wide at the bottom centre in landscape. Choosing a row
 * hands it back to the caller, which opens that source.
 *
 * Tapping outside dismisses it, without a scrim: a full-screen layer would
 * take the touches a pinch on the video needs (spec 11.2). So a touch outside
 * closes the list, and is swallowed (it does not also press what is under
 * it), except on a video stage (data-zoom-surface), where it carries on so
 * pinch and pan still work.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { usePhoneLayout } from './usePhoneLayout';
import { IosSheet, SheetTextButton } from './IosSheet';

export interface CompareSheetRow {
  key: string;
  label: string;
  icon: ReactNode;
  available: boolean;
  /** Trailing note when unavailable ("None", "N/A"). */
  note?: string;
  onPick: () => void;
}

export function CompareSheet({ rows, onClose, children }: { rows: CompareSheetRow[]; onClose: () => void; children?: ReactNode }) {
  const landscape = usePhoneLayout().landscape;
  const cardRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    /* The tap that closes the list must not also press what is under it.
       The list unmounts on the pointerdown, before the click arrives, so the
       swallow is a one-shot listener on the document that outlives it. */
    const swallowNextClick = () => {
      const eat = (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
      document.addEventListener('click', eat, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', eat, true), 700);
    };
    const down = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (!t || cardRef.current?.contains(t)) return;
      if (!t.closest('[data-zoom-surface]')) {
        e.preventDefault();
        e.stopPropagation();
        swallowNextClick();
      }
      onCloseRef.current();
    };
    /* Attached on the next tick, so the tap that opened the list does not
       close it. */
    const id = setTimeout(() => document.addEventListener('pointerdown', down, true), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', down, true);
    };
  }, []);

  /* Spec 14.4: an iOS sheet (IosSheet) with Cancel on the left and the title
     centred; the sources in an inset grouped list, 60 rows (44 in
     landscape, which keeps the sheet short), a 16:9 tile, title Body 17/22,
     note Subhead 15/20 in grey, separators from the text. */
  const rowH = landscape ? 44 : 60;
  const tile = landscape ? { w: 48, h: 27 } : { w: 64, h: 36 };
  return (
    <IosSheet
      cardRef={cardRef}
      testId="compare-sheet"
      title="Compare with"
      landscape={landscape}
      onDismiss={onClose}
      leading={<SheetTextButton label="Cancel" onClick={onClose} />}
    >
      <div className="lve-group" style={{ ['--lve-sep-inset' as string]: `${16 + tile.w + 12}px` }}>
        {rows.map((r) => (
          <button
            key={r.key}
            type="button"
            disabled={!r.available}
            onClick={r.onPick}
            className="w-full flex items-center gap-3 px-4 text-left active:bg-white/10 disabled:opacity-40"
            style={{ height: rowH, color: 'var(--editor-text)' }}
          >
            <span
              className="flex-none flex items-center justify-center rounded-[6px]"
              style={{ width: tile.w, height: tile.h, background: 'var(--editor-timeline)', color: r.available ? 'var(--color-db-oasis)' : 'var(--editor-text-muted)' }}
            >
              {r.icon}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block [overflow-wrap:anywhere] text-[17px] leading-[22px]">{r.label}</span>
              {!r.available && r.note && <span className="block [overflow-wrap:anywhere] text-[15px] leading-[20px] text-[var(--editor-text-muted)]">{r.note}</span>}
            </span>
          </button>
        ))}
      </div>
      {children}
    </IosSheet>
  );
}
